import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Modal,
  notification,
  Popconfirm,
  Tag,
  Tooltip,
} from "antd";
import {
  UserSwitchOutlined,
  LockOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import ModuleSelctorCards from "../../../../Components/Cards/ModuleSelectorCards";
import FuelIcon from "../../../../Assets/Icons/fuel-icon.svg";
import ClipboardIcon from "../../../../Assets/Icons/clipboard.svg";
import CarPartsIcon from "../../../../Assets/Icons/car_repair.svg";
import creditIcon from "../../../../Assets/Icons/garage_money.svg";
import paymentIcon from "../../../../Assets/Icons/payments.svg";
import dataIcon from "../../../../Assets/Icons/data_usage.svg";
import legalIcon from "../../../../Assets/Icons/apartment.svg";
import dateIcon from "../../../../Assets/Icons/calendar_month.svg";
import regIcon from "../../../../Assets/Icons/markdown_copy.svg";
import currencyIcon from "../../../../Assets/Icons/attach_money.svg";
import contactIcon from "../../../../Assets/Icons/account_child_invert.svg";
import mailIcon from "../../../../Assets/Icons/mail.svg";
import locIcon from "../../../../Assets/Icons/distance.svg";
import keyIcon from "../../../../Assets/Icons/password_2.svg";
import GenericTable from "../../../../Components/Tables/GenericTable";
import docIcon from "../../../../Assets/Icons/document.svg";
import pdfIcon from "../../../../Assets/Icons/pdf.svg";
import GenericModal from "../../../../Components/Modals/GenericModal";

import "./style.css";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import {
  getFleetManagerById,
  updateFleetCompanyFuelingStatus,
  loginAsFleetManager,
} from "../../../../API/Admin/FleetManagement";
import { deleteDocument } from "../../../../API/Documents";
import { message } from "antd";
import { UserContext } from "../../../../App";
import updateUserStates, {
  setStoredToken,
  setStoredUser,
} from "../../../../Utlis/UpdateUserState";
import getPortalByTenantType from "../../../../Utlis/GetPortalByTenantType";
import getFirstAllowedRoute from "../../../../Utlis/GetFirstAllowedRoute";
import usePageAccess from "../../../../Utlis/usePageAccess";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-US");
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const formatLabel = (str) =>
  str
    ? str
        .split("_")
        .map((word) => capitalize(word))
        .join(" ")
    : str;

const BADGE_COLORS = [
  { background: "#4f46e51f", color: "#4f46e5" },
  { background: "#fee2e2", color: "#dc2626" },
  { background: "#ecfdf5", color: "#059669" },
  { background: "#ede9fe", color: "#7c3aed" },
  { background: "#f59e0b1f", color: "#f59e0b" },
  { background: "#dbeafe", color: "#3b82f6" },
  { background: "#ea580c1a", color: "#ea580c" },
  { background: "#fdf2f8", color: "#db2777" },
  { background: "#f0fdfa", color: "#0d9488" },
];

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const getModuleBadgeStyle = (name) => {
  if (!name || name === "-") {
    return { background: "#f4f4f5", color: "#71717a" };
  }
  if (name.toLowerCase() === "fuel") {
    return { background: "#0000000f", color: "#000000" };
  }
  return BADGE_COLORS[hashString(name) % BADGE_COLORS.length];
};

const MODULE_ICONS_BY_CODE = {
  fuel: FuelIcon,
  maintenance: ClipboardIcon,
  parts: CarPartsIcon,
};

const PRICING_TABS = ["Monthly Pricing", "Annually Pricing"];

const cycleToPeriod = (cycle) =>
  cycle === "annual"
    ? "Annually Pricing"
    : cycle === "monthly"
      ? "Monthly Pricing"
      : null;

const buildModuleOption = (mod, isAnnual) => {
  const iconSrc = mod.icon
    ? resolveAvatarUrl(mod.icon)
    : MODULE_ICONS_BY_CODE[mod.code?.toLowerCase()] || ClipboardIcon;
  const rawPrice = isAnnual
    ? mod.annual_price_per_vehicle
    : mod.monthly_price_per_vehicle;
  const price = parseFloat(rawPrice);

  return {
    id: mod.id,
    title: mod.name,
    description: mod.description,
    price: rawPrice
      ? price
        ? `${mod.currency || "JMD"} ${price.toFixed(2)}/vehicle`
        : "Free"
      : undefined,
    icon: <img src={iconSrc} alt={mod.name} />,
  };
};

const DEFAULT_MANAGER = {
  company: "",
  key: "",
  avatar: null,
  assignModule: "",
  status: "",
  legalName: "-",
  registrationNumber: "-",
  contactEmail: "-",
  contactPhone: "-",
  address: "-",
  apiKeys: [],
  currency: "-",
  primaryContact: "-",
  designation: "-",
  secondaryContactPerson: "-",
  secondaryDesignation: "-",
  secondaryContactEmail: "-",
  secondaryContactPhone: "-",
  vehicleCount: 0,
  driverCount: 0,
  creditBalance: "$0.00",
  creditUsed: "$0.00",
  fuelingLocked: false,
  registrationDate: "-",
  dateIncorporated: "-",
};

const FleetManagementDetail = ({ setUser }) => {
  const { canEdit } = usePageAccess("fleet-management");
  const navigate = useNavigate();
  const location = useLocation();
  const passedRecord = location.state?.record;
  const { setUserData, setUserToken } = useContext(UserContext);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFuelModalOpen, setIsFuelModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [loggingInAs, setLoggingInAs] = useState(false);
  const [reason, setReason] = useState("");
  const [manager, setManager] = useState({
    ...DEFAULT_MANAGER,
    company: passedRecord?.company || "",
    key: passedRecord?.key || "",
    avatar: passedRecord?.logo || null,
    assignModule: passedRecord?.assignModule || "",
    status: passedRecord?.status || "",
  });
  const [documents, setDocuments] = useState([]);
  const [subscriptionPaymentData, setSubscriptionPaymentData] = useState([]);
  const [activeModuleFilters, setActiveModuleFilters] = useState(null);
  const [assignedModulesRaw, setAssignedModulesRaw] = useState([]);
  const [pricingPeriod, setPricingPeriod] = useState(PRICING_TABS[0]);
  const [planPricingPeriod, setPlanPricingPeriod] = useState(null);

  useEffect(() => {
    if (!passedRecord?.key) return;

    const fetchManager = async () => {
      try {
        const res = await getFleetManagerById(passedRecord.key);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          setManager({
            company: data.legal_name || "-",
            key: String(data.id ?? passedRecord.key),
            avatar: resolveAvatarUrl(data.avatar),
            assignModule:
              Array.isArray(data.assigned_modules) &&
              data.assigned_modules.length
                ? data.assigned_modules.map((mod) => mod.name || mod).join(", ")
                : "",
            status: data.fueling_locked ? "Inactive" : "Active",
            legalName: data.legal_name || "-",
            registrationNumber: data.registration_number || "-",
            contactEmail: data.contact_email || "-",
            contactPhone: data.contact_phone || "-",
            address: data.address || "-",
            apiKeys: Array.isArray(data.navixy_api_keys)
              ? data.navixy_api_keys
              : [],
            currency: data.currency || "-",
            primaryContact: data.primary_contact_person || "-",
            designation: data.designation || "-",
            secondaryContactPerson: data.secondary_contact_person || "-",
            secondaryDesignation: data.secondary_designation || "-",
            secondaryContactEmail: data.secondary_contact_email || "-",
            secondaryContactPhone: data.secondary_contact_phone || "-",
            vehicleCount: data.vehicle_count ?? 0,
            driverCount: data.driver_count ?? 0,
            creditBalance:
              data.credit_balance != null ? `$${data.credit_balance}` : "-",
            creditUsed: data.credit_used != null ? `$${data.credit_used}` : "-",
            fuelingLocked: !!data.fueling_locked,
            registrationDate: formatDate(data.registration_date),
            dateIncorporated: formatDate(data.date_incorporated),
          });
          setIsLocked(!!data.fueling_locked);
          setReason(data.fueling_locked_reason || "");

          setAssignedModulesRaw(
            Array.isArray(data.assigned_modules) ? data.assigned_modules : [],
          );
          const planPeriod = cycleToPeriod(
            data.assigned_modules?.[0]?.billing_cycle,
          );
          setPlanPricingPeriod(planPeriod);
          if (planPeriod) setPricingPeriod(planPeriod);

          if (Array.isArray(data.documents)) {
            setDocuments(
              data.documents.map((doc, idx) => ({
                key: String(doc.id ?? idx),
                id: doc.id,
                name: doc.file ? doc.file.split("/").pop() : "-",
                category: doc.category || "-",
                allowedFormats: doc.allowed_formats || [],
                date: formatDate(doc.uploaded_at),
                expiryDate: formatDate(doc.expiry_date),
                fileUrl: resolveAvatarUrl(doc.file),
              })),
            );
          }

          if (Array.isArray(data.subscription_payments)) {
            setSubscriptionPaymentData(
              data.subscription_payments.map((p, idx) => ({
                key: String(p.id ?? idx),
                date: p.date || "-",
                txnId: p.txn_id || "-",
                fleetManager: p.fleet_manager || data.legal_name || "-",
                vehicles: p.vehicles ?? "-",
                module: Array.isArray(p.modules)
                  ? p.modules.map((mod) => mod.name).join(", ")
                  : "-",
                subPlan: p.sub_plan || capitalize(p.billing_cycle) || "-",
                amount: p.amount ?? 0,
                paymentMethod: formatLabel(p.payment_method) || "-",
                paidBy: p.paid_by || "-",
                proof: p.proof || "-",
                status: p.status || "-",
                fileUrl: resolveAvatarUrl(p.file_url),
                logo: resolveAvatarUrl(p.logo) || resolveAvatarUrl(data.avatar),
              })),
            );
          }
        } else {
          message.error(res?.data?.message || "Failed to load fleet manager.");
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load fleet manager.",
        );
      }
    };

    fetchManager();
  }, [passedRecord?.key]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleLoginAsFleetManager = async () => {
    if (!manager.key) return;

    setLoggingInAs(true);
    try {
      const res = await loginAsFleetManager(manager.key);
      const sessionData = res?.data?.data;

      if (Math.floor(res?.status / 100) === 2 && sessionData?.access) {
        localStorage.clear();
        setStoredToken({
          access: sessionData.access,
          refresh: sessionData.refresh,
        });
        setStoredUser(sessionData);
        updateUserStates(setUserData, setUserToken);

        const portal = getPortalByTenantType(sessionData.tenant_type) || {
          userType: "manager",
          dashboardRoute: "/dashboard-manager",
        };
        setUser?.(portal.userType);

        notification.success({
          message: "Logged in as Fleet Manager",
          placement: "topRight",
        });

        const landingRoute = getFirstAllowedRoute(
          portal.userType,
          sessionData.page_access,
          portal.dashboardRoute,
        );
        navigate(
          sessionData.subscription_payment_required
            ? "/my-subscription"
            : landingRoute,
        );
      } else {
        notification.error({
          message: "Login Failed",
          description:
            res?.data?.message || "Failed to login as fleet manager.",
          placement: "topRight",
        });
      }
    } catch (err) {
      notification.error({
        message: "Login Failed",
        description:
          err?.response?.data?.message || "Failed to login as fleet manager.",
        placement: "topRight",
      });
    } finally {
      setLoggingInAs(false);
    }
  };

  const handleEditCompany = () => {
    navigate("/edit-company-onboarding", { state: { manager, documents } });
  };

  const handleView = (record) => {
    if (!record?.fileUrl) return;

    const lowerName = record.name.toLowerCase();

    if (lowerName.endsWith(".pdf")) {
      // Open PDF in new tab
      window.open(record.fileUrl, "_blank", "noopener,noreferrer");
    } else {
      // Force download for other file types
      const link = document.createElement("a");
      link.href = record.fileUrl;
      link.download = record.name; // forces download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDeleteDocument = (record) => {
    setSelectedRow(record);
    setIsDeleteModalOpen(true);
  };

  const handleToggleFueling = () => {
    if (!isLocked) {
      setIsFuelModalOpen(true);
    }
  };

  const handleConfirmLock = async (enteredReason) => {
    if (!manager.key) return;

    try {
      const res = await updateFleetCompanyFuelingStatus(manager.key, {
        fueling_locked: true,
        reason: enteredReason,
      });

      if (Math.floor(res?.status / 100) === 2) {
        setIsLocked(true);
        setReason(enteredReason);
        setManager((prev) => ({ ...prev, status: "Inactive" }));
        setIsFuelModalOpen(false);

        notification.success({
          message: "Fuel Locked",
          description: "Fueling has been locked successfully!",
          placement: "topRight",
        });
      } else {
        message.error(res?.data?.message || "Failed to lock fueling.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to lock fueling.");
    }
  };

  const handleEnableFueling = async () => {
    if (!manager.key) return;

    try {
      const res = await updateFleetCompanyFuelingStatus(manager.key, {
        fueling_locked: false,
      });

      if (Math.floor(res?.status / 100) === 2) {
        setIsLocked(false);
        setReason("");
        setManager((prev) => ({ ...prev, status: "Active" }));

        notification.success({
          message: "Fuel Enabled",
          description: "Fueling has been enabled successfully!",
          placement: "topRight",
        });
      } else {
        message.error(res?.data?.message || "Failed to enable fueling.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to enable fueling.",
      );
    }
  };
  const columns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      enableSearch: true,
      render: (name) => {
        let icon = docIcon;
        const lowerName = name.toLowerCase();

        if (lowerName.endsWith(".pdf")) icon = pdfIcon;
        else if (
          lowerName.endsWith(".doc") ||
          lowerName.endsWith(".docx") ||
          lowerName.endsWith(".coc")
        )
          icon = docIcon;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src={icon} alt="doc icon" style={{ width: 20, height: 20 }} />
            <span>{name}</span>
          </div>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      filters: [
        { text: "License", value: "License" },
        { text: "Tax", value: "Tax" },
        { text: "Insurance", value: "Insurance" },
      ],
      onFilter: (value, record) => record.category === value,
      render: (category) => {
        let badgeClass = "";

        switch (category.toLowerCase()) {
          case "license":
            badgeClass = "badge-license";
            break;
          case "tax":
            badgeClass = "badge-tax";
            break;
          case "insurance":
            badgeClass = "badge-insurance";
            break;
          default:
            badgeClass = "badge-default";
        }

        return (
          <span className={`badge-category ${badgeClass}`}>{category}</span>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Verified", value: "Verified" },
        { text: "Active", value: "Active" },
        { text: "Pending", value: "Pending" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        let statusClass = "";

        if (status === "Verified") statusClass = "status-verified";
        else if (status === "Active") statusClass = "status-active";
        else statusClass = "status-pending";

        return <span className={statusClass}>{status}</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          canEdit && {
            key: "delete",
            label: "Remove",
            style: { color: "#DC2626", fontWeight: 500 },
            icon: <DeleteOutlined />,
            onClick: () => handleDeleteDocument(record),
          },
          {
            key: "view",
            label: "View",
            icon: <EyeOutlined />,
            style: { color: "#000000", fontWeight: 500 },
            onClick: () => handleView(record),
          },
        ].filter(Boolean);
        return (
          <>
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  width: 24,
                  height: 24,
                }}
              >
                <BsThreeDotsVertical
                  style={{ fontSize: 20, color: "#4F46E5" }}
                />
              </span>
            </Dropdown>
          </>
        );
      },
    },
  ];

  const getUniqueFilters = (data, key) =>
    [...new Set(data.map((item) => item[key]))].map((value) => ({
      text: value,
      value,
    }));

  const moduleFilters = [
    ...new Set(
      subscriptionPaymentData.flatMap((item) => item.module.split(", ")),
    ),
  ].map((value) => ({ text: value, value }));
  const subPlanFilters = getUniqueFilters(subscriptionPaymentData, "subPlan");
  const paymentMethodFilters = getUniqueFilters(
    subscriptionPaymentData,
    "paymentMethod",
  );
  const statusFilters = getUniqueFilters(subscriptionPaymentData, "status");

  const subscriptionPaymentColumns = [
    {
      title: "Date/Time",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
      render: (text) => {
        const parts = text.split(" ");
        const datePart = parts[0];
        const timePart = parts[1];

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: "1.2",
            }}
          >
            <span>{datePart}</span>
            <span
              style={{
                color: "#71717A",
              }}
            >
              {timePart}
            </span>
          </div>
        );
      },
    },
    {
      title: "Txn Id",
      dataIndex: "txnId",
      key: "txnId",
      enableSearch: "true",
    },
    {
      title: "Fleet Manager",
      dataIndex: "fleetManager",
      key: "fleetManager",
      enableSearch: true,
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src={record.logo}
            alt={text}
            style={{ width: 24, height: 24, borderRadius: "5px" }}
          />
          {text}
        </span>
      ),
    },
    {
      title: "Vehicles",
      dataIndex: "vehicles",
      key: "vehicles",
      sorter: (a, b) =>
        (parseFloat(a.vehicles) || 0) - (parseFloat(b.vehicles) || 0),
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      filters: moduleFilters,
      onFilter: (value, record) => record.module.split(", ").includes(value),
      render: (module) => {
        const names = module.split(", ");
        const visibleNames =
          activeModuleFilters && activeModuleFilters.length
            ? names.filter((name) => activeModuleFilters.includes(name))
            : names;

        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {visibleNames.map((name) => (
              <span
                key={name}
                className="module-badge"
                style={getModuleBadgeStyle(name)}
              >
                {name}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Sub Plan",
      dataIndex: "subPlan",
      key: "subPlan",
      filters: subPlanFilters,
      onFilter: (value, record) => record.subPlan === value,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => a.amount - b.amount,
      render: (amount) => <span style={{ fontWeight: 600 }}>${amount}</span>,
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      filters: paymentMethodFilters,
      onFilter: (value, record) => record.paymentMethod === value,
      render: (method) => <span className="fuel-type-badge">{method}</span>,
    },
    {
      title: "Paid By",
      dataIndex: "paidBy",
      key: "paidBy",
      enableSearch: true,
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src={record.logo}
            alt={text}
            style={{ width: 24, height: 24, borderRadius: "5px" }}
          />
          {text}
        </span>
      ),
    },
    {
      title: "Payment Proof",
      dataIndex: "proof",
      key: "proof",
      render: (_, record) => (
        <button
          className="proof-link"
          onClick={() => {
            if (record.fileUrl) {
              window.open(record.fileUrl, "_blank", "noopener,noreferrer");
            }
          }}
        >
          <span className="proof-icon">
            <DownloadOutlined />
          </span>
          {record.proof}
        </button>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-badge ${status.toLowerCase()}`}>{status}</span>
      ),
    },
  ];

  const isAnnual = pricingPeriod === "Annually Pricing";
  const assignedModules = assignedModulesRaw.map((mod) =>
    buildModuleOption(mod, isAnnual),
  );

  return (
    <div className="fleet-detail">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="header-wrapper">
        <div className="title">Fleet Manager</div>
        <div className="btns">
          {canEdit && (
            <div>
              <Button
                icon={<UserSwitchOutlined />}
                className="login-btn"
                onClick={handleLoginAsFleetManager}
                loading={loggingInAs}
              >
                Login as Fleet Manager
              </Button>
            </div>
          )}
          {canEdit && (
            <div>
              {!isLocked ? (
                <Button
                  className="fuel-btn"
                  icon={<LockOutlined />}
                  onClick={handleToggleFueling}
                >
                  Lock Fueling
                </Button>
              ) : (
                <Popconfirm
                  title="Are you sure you want to enable fueling?"
                  onConfirm={handleEnableFueling}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    className="fuel-btn enabled"
                    icon={<LockOutlined />}
                    style={{
                      color: "#16a34a",
                      backgroundColor: "#16a34a1a",
                    }}
                  >
                    Fueling Enabled
                  </Button>
                </Popconfirm>
              )}
            </div>
          )}

          {canEdit && (
            <div>
              <Button
                icon={<EditOutlined />}
                type="primary"
                className="edit-btn"
                onClick={handleEditCompany}
              >
                Edit Company
              </Button>
            </div>
          )}
        </div>
      </div>
      {isLocked && (
        <div className="locked-info">
          <div className="label">
            Locked Reason
            <div className="reason"> {reason}</div>
          </div>
        </div>
      )}
      <GenericModal
        open={isFuelModalOpen}
        title="Lock Fueling"
        subtitle="Please provide a reason for locking fueling"
        showReason={true}
        confirmText="Confirm Lock"
        cancelText="Cancel"
        iconColor="#ef4444"
        iconBgColor="#ef44441a"
        confirmBtnColor="#EA580C"
        onCancel={() => setIsFuelModalOpen(false)}
        onConfirm={handleConfirmLock}
      />

      <div className="row">
        <div className="left-column">
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar">
                {manager.avatar ? (
                  <img
                    src={manager.avatar}
                    alt={manager.company}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  manager.company?.charAt(0).toUpperCase()
                )}
              </div>
            </div>

            <div className="profile-body">
              <div className="company-name">{manager.company}</div>
              <div className="company-id">ID: {manager.key}</div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                {manager.assignModule &&
                  manager.assignModule.split(", ").map((name) => (
                    <span key={name} className="badge badge-fuel">
                      {name}
                    </span>
                  ))}
                <span
                  className={`badge ${manager.status === "Active" ? "badge-active" : "badge-locked"}`}
                >
                  {manager.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="tabs">
            <GenericStatCard
              title="Credit Balance"
              value={manager.creditBalance}
              icon={creditIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Fuel Status"
              status={isLocked ? "Inactive" : "Active"}
              showBlankLine={true}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Total Vehicles"
              value={String(manager.vehicleCount)}
              subtitle=""
              icon={paymentIcon}
              showBlankLine={true}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Total Drivers"
              value={String(manager.driverCount)}
              icon={dataIcon}
              showBlankLine={true}
            />
          </div>
        </div>
      </div>

      <div className="info">
        <div className="info-card">
          <div className="info-grid">
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={legalIcon} alt="Fuel Icon" />
                </div>
                <div className="info-label">Legal Name</div>
              </div>
              <div className="info-value">{manager.legalName}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={dateIcon} alt="Fuel Icon" />
                </div>
                <div className="info-label">Date Incorporated</div>
              </div>
              <div className="info-value">{manager.dateIncorporated}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={regIcon} alt="Fuel Icon" />
                </div>
                <div className="info-label">Registration Number</div>
              </div>
              <div className="info-value">{manager.registrationNumber}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={currencyIcon} alt="Curr Icon" />
                </div>
                <div className="info-label">Currency</div>
              </div>
              <div className="info-value">{manager.currency}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={contactIcon} alt="Curr Icon" />
                </div>
                <div className="info-label">Primary Contact</div>
              </div>
              <div className="info-value">{manager.primaryContact}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={mailIcon} alt="Curr Icon" />
                </div>
                <div className="info-label">Contact Email</div>
              </div>
              <div className="info-value">{manager.contactEmail}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={locIcon} alt="lov Icon" />
                </div>
                <div className="info-label">Location</div>
              </div>
              <div className="info-value">{manager.address}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={keyIcon} alt="API Key Icon" />
                </div>
                <div className="info-label">API Key</div>
              </div>
              <div className="info-value api-keys-value">
                {manager.apiKeys?.length
                  ? manager.apiKeys.map((key) => (
                      <Tooltip
                        key={key}
                        title={<span style={{ wordBreak: "break-all" }}>{key}</span>}
                      >
                        <Tag className="api-key-tag">{key}</Tag>
                      </Tooltip>
                    ))
                  : "-"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="info assigned-modules">
        {assignedModules.length ? (
          <>
            <div className="report-navigation">
              <div className="nav-tabs">
                {PRICING_TABS.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`nav-item ${pricingPeriod === tab ? "active" : ""}`}
                    disabled={!!planPricingPeriod && tab !== planPricingPeriod}
                    onClick={() => setPricingPeriod(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            <ModuleSelctorCards
              options={assignedModules}
              title="Assigned Modules"
              allowSelection={false}
              multiple={true}
              selectedIds={assignedModules.map((mod) => mod.id)}
              showFooter={true}
              footerLabel={isAnnual ? "Annual Rate" : "Monthly Rate"}
            />
          </>
        ) : (
          <div className="info-card no-module-assigned">No module assigned</div>
        )}
      </div>

      <div className="document-table-fleet">
        <GenericDocumentManagerTable
          initialDocuments={documents}
          setDocuments={setDocuments}
          showTableOnly={true}
          hideRemoveAction={true}
          hideDateColumn={true}
          onDeleteDocument={async (doc) => {
            if (!doc?.id) return;

            const res = await deleteDocument(doc.id);

            if (Math.floor(res?.status / 100) !== 2) {
              throw new Error(
                res?.data?.message || "Failed to delete document.",
              );
            }
          }}
        />

        <div className="subscription-payments">
          <GenericTable
            title="Subscription Payment History"
            dataSource={subscriptionPaymentData}
            columns={subscriptionPaymentColumns}
            enableRowSelection={false}
            onTableChange={(filters) =>
              setActiveModuleFilters(filters.module || null)
            }
          />
        </div>

        <GenericModal
          open={isDeleteModalOpen}
          title="Remove Document"
          subtitle={`Are you sure you want to remove ${selectedRow?.name}?`}
          highlightText={selectedRow?.name}
          showReason={false}
          confirmText="Delete"
          cancelText="Cancel"
          iconColor="#DC2626"
          iconBgColor="#FEE2E2"
          confirmBtnColor="#DC2626"
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={() => {
            setIsDeleteModalOpen(false);
            setDocuments((prev) =>
              prev.filter((doc) => doc.key !== selectedRow.key),
            );
            notification.success({
              message: "Document Deleted",
              description: `${selectedRow?.name} has been successfully removed.`,
              placement: "topRight",
            });
          }}
        />
      </div>
    </div>
  );
};

export default FleetManagementDetail;
